/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {BolmateSidebarItem, IdleState} from '../../util/interfaces';
import {onChangeDashboardTab, onResetDashboard} from '../../util/events';
import {sendGaEvent} from '../../util/analytics';
import {useLocation, useNavigate} from 'react-router';
import {isAbsoluteLink, ResponseData, stopPropagation, useEvent} from '@dvrd/dvr-controls';
import {AppContext} from '../misc/appContext';
import {getSidebarItems} from '../../routes';
import {getOpenInvoiceRequestCount} from '../../util/requestSender';
import {useIdleObserver} from '../misc/idleObserver';
import SidebarMenu from './sidebarMenu';
import {useIsFirstRender} from '../../util/componentUtil';
import {setOpenInvoiceRequestCount} from '../../util/invoiceRequestUtil';
import {requestPageChangePermission} from '../../util/pageChanger';

function sendMenuEvent(id: string) {
    const label = {
        'dashboard': 'Dashboard',
        'settings': 'Instellingen',
        'pdf': 'PDF Template',
        'contact': 'Contact',
        'login': 'Inloggen'
    }[id];
    if (label) sendGaEvent('Click', 'Menu', label);
}

const LOAD_DELAY = 60000; // 60 seconds

function itemIsActive(item: BolmateSidebarItem, pathname: string): boolean {
    if (Array.isArray(item.route) && item.route.includes(pathname)) return true;
    else if (item.route === pathname) return true;
    else if (typeof item.route === 'string' && item.exactRoute === false && pathname.includes(item.route)) return true;
    return false;
}

export default function SidebarMenuController() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const [numRequests, setNumRequests] = useState(-1);
    const [activeItems, setActiveItems] = useState<Array<string>>([]);
    const numRequestsTimeout = useRef<number | null>(null);
    const numRequestsAbort = useRef<AbortController | null>(null);
    const items = useMemo(() => {
        return getSidebarItems(context.userContext.user, context.accountContext.account);
    }, [context.userContext.user, context.accountContext.account]);
    const location = useLocation();
    const isFirstRender = useIsFirstRender();
    useEvent('updateInvoiceRequestCount', onUpdateInvoiceRequestCount);
    useIdleObserver(onIdleStateChange);

    function onUpdateInvoiceRequestCount(count: number) {
        setNumRequests(count);
    }

    function onLocationChange() {
        const {pathname} = location;
        const activeItem = getActiveItem(items, pathname);
        if (activeItem) {
            const activeItems = [activeItem.id];
            if (activeItem.parentId) activeItems.push(activeItem.parentId);
            setActiveItems(activeItems);
        }
    }

    function onClickItem(item: BolmateSidebarItem) {
        return function (evt: React.MouseEvent) {
            stopPropagation(evt);
            const {route, id, disabled, onClick} = item;
            if (disabled) return;
            evt.persist();
            requestPageChangePermission(() => {
                sendMenuEvent(id);
                if (route) {
                    if (Array.isArray(item.route)) onNavigateRoute(item.route[0]);
                    else onNavigateRoute(route as string);
                } else if (item.id === 'contact')
                    window.open('https://bolmate.nl/contact/', '_blank');
                if (item.id === 'orders')
                    onResetDashboard();
                onClick?.(evt);
            });
        };
    }

    function onNavigateRoute(route: string) {
        if (isAbsoluteLink(route))
            window.open(route, '_blank');
        else navigate(route);
    }

    function getActiveItem(items: BolmateSidebarItem[], pathname: string): BolmateSidebarItem | null {
        for (const item of items) {
            if (itemIsActive(item, pathname)) return item;
            if (Array.isArray(item.children)) {
                const activeItem = getActiveItem(item.children, pathname);
                if (activeItem) return activeItem;
            }
        }
        return null;
    }

    function loadNumRequests(isInitial: boolean = false) {
        cleanup();
        const {account} = context.accountContext;
        if (!account?.hasActiveAPIKey()) return;
        window.setTimeout(() => {
            getOpenInvoiceRequestCount({
                account_id: account.id, callback: (data: ResponseData) => {
                    if (data.success) {
                        setNumRequests(data.count);
                        setOpenInvoiceRequestCount(data.count);
                        if (isInitial && data.count) {
                            navigate('/facturen/aanvragen');
                            onChangeDashboardTab();
                        }
                        numRequestsTimeout.current = window.setTimeout(loadNumRequests, LOAD_DELAY);
                    }
                }
            });
        });
    }

    function onIdleStateChange(state: IdleState) {
        if (state === IdleState.IDLE) clearNumRequestTimeout();
        else loadNumRequests();
    }

    function cleanup() {
        clearNumRequestTimeout();
        numRequestsAbort.current?.abort();
        numRequestsAbort.current = null;
    }

    function clearNumRequestTimeout() {
        if (numRequestsTimeout.current !== null) {
            window.clearTimeout(numRequestsTimeout.current);
            numRequestsTimeout.current = null;
        }
    }

    useEffect(() => {
        if (!isFirstRender)
            loadNumRequests();
        return function () {
            cleanup();
        };
    }, [context.accountContext.account]);

    useEffect(() => {
        onLocationChange();
    }, [location.pathname]);

    useEffect(() => {
        loadNumRequests(false);
    }, []);

    return (
        <SidebarMenu onClickItem={onClickItem} items={items} numOpenRequests={numRequests} activeItems={activeItems}/>
    );
}